
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ApiProvider {
  id: string;
  name: string;
  code: string;
  base_url: string;
  auth_type: 'oauth2' | 'api_key' | 'custom_token';
  auth_url?: string;
  token_url?: string;
  client_id?: string;
  client_secret?: string;
  scope?: string;
  is_active?: boolean;
  is_default?: boolean;
  logo_url?: string;
  created_at?: string;
  updated_at?: string;
  endpoints?: ApiEndpoint[]; // Add endpoints property
}

export interface ApiEndpoint {
  id: string;
  provider_id: string;
  name: string;
  path: string;
  method: string;
  description?: string;
  request_schema?: any;
  response_schema?: any;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export function useApiProviders() {
  const fetchApiProviders = async (): Promise<ApiProvider[]> => {
    const { data, error } = await supabase
      .from('api_providers')
      .select('*, api_endpoints(*)')
      .order('name');
    
    if (error) throw error;
    
    // Transform the data to match our interface
    return (data || []).map(provider => ({
      ...provider,
      auth_type: provider.auth_type as 'oauth2' | 'api_key' | 'custom_token',
      endpoints: provider.api_endpoints
    }));
  };

  return useQuery({
    queryKey: ['api-providers'],
    queryFn: fetchApiProviders,
  });
}

export function useApiProvider(id: string | undefined) {
  const fetchApiProvider = async (): Promise<ApiProvider | null> => {
    if (!id) return null;
    
    const { data, error } = await supabase
      .from('api_providers')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    
    return {
      ...data,
      auth_type: data.auth_type as 'oauth2' | 'api_key' | 'custom_token',
      endpoints: [] // Add empty endpoints array as default
    };
  };

  return useQuery({
    queryKey: ['api-provider', id],
    queryFn: fetchApiProvider,
    enabled: !!id,
  });
}

export function useCreateApiProvider() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (provider: Omit<ApiProvider, 'id'>) => {
      const { data, error } = await supabase
        .from('api_providers')
        .insert(provider)
        .select()
        .single();
      
      if (error) throw error;
      return {
        ...data,
        auth_type: data.auth_type as 'oauth2' | 'api_key' | 'custom_token'
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-providers'] });
      toast.success('API provider created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create API provider: ${error.message}`);
    },
  });
}

export function useUpdateApiProvider() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ApiProvider> & { id: string }) => {
      const { data, error } = await supabase
        .from('api_providers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['api-providers'] });
      queryClient.invalidateQueries({ queryKey: ['api-provider', variables.id] });
      toast.success('API provider updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update API provider: ${error.message}`);
    },
  });
}

export function useDeleteApiProvider() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('api_providers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-providers'] });
      toast.success('API provider deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete API provider: ${error.message}`);
    },
  });
}

export function useApiEndpoints(providerId?: string) {
  const fetchApiEndpoints = async (): Promise<ApiEndpoint[]> => {
    let query = supabase
      .from('api_endpoints')
      .select('*')
      .order('name');
    
    if (providerId) {
      query = query.eq('provider_id', providerId);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    return data || [];
  };

  return useQuery({
    queryKey: ['api-endpoints', providerId],
    queryFn: fetchApiEndpoints,
    enabled: !!providerId,
  });
}

export function useCreateApiEndpoint() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (endpoint: Omit<ApiEndpoint, 'id'>) => {
      const { data, error } = await supabase
        .from('api_endpoints')
        .insert(endpoint)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-endpoints', data.provider_id] });
      toast.success('API endpoint created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create API endpoint: ${error.message}`);
    },
  });
}

export function useUpdateApiEndpoint() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ApiEndpoint> & { id: string }) => {
      const { data, error } = await supabase
        .from('api_endpoints')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-endpoints', data.provider_id] });
      toast.success('API endpoint updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update API endpoint: ${error.message}`);
    },
  });
}

export function useDeleteApiEndpoint() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, providerId }: { id: string; providerId: string }) => {
      const { error } = await supabase
        .from('api_endpoints')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { id, providerId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-endpoints', data.providerId] });
      toast.success('API endpoint deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete API endpoint: ${error.message}`);
    },
  });
}
